import { useEffect, useRef } from "react";
import { CommentPayload, CommentType } from "../../../rte/Annotations";
import RichTextWrapper, { Props as RteProps } from "../../../rte/ExternalApi";
import { toStf } from "../../../rte/ScorableTextFormat";
import { ToolbarOptions } from "../../../rte/Toolbar";
import { RteContent, setRteProps } from "../../../app/rteUtils";
import { htmlDecode } from "../../../app/utils";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import {
	RteExtendedComment,
	SyncDraftCommentRequest,
	ExtendedCommentType,
	useSyncDraftCommentsMutation,
} from "../../scoreReport/scoreReportApiSlice";
import { CommentedEssayViewModel } from "./draftingApiSlice";

export interface Props {
	commentedEssay: CommentedEssayViewModel;
	isScoreReport?: boolean;
	showAnnotations?: boolean;
}

function getExtendedCommentType(
	commentType: CommentType,
	source: "essay" | "sources"
): ExtendedCommentType {
	if (source === "essay") return commentType;
	switch (commentType) {
		case "comment":
			return "sourcescomment";
		case "grammar":
			return "sourcesgrammar";
		case "spelling":
			return "sourcesspelling";
	}
	return commentType;
}

function getRteComments(rte: RichTextWrapper, source: "essay" | "sources"): RteExtendedComment[] {
	if (!rte) return [];
	const stf = toStf(rte.state.editor, rte.state.annotations);

	const comments = stf.annotations
		.filter((a) => a.payload.type === "comment")
		.map((a) => {
			const payload = a.payload as CommentPayload;
			return {
				from: a.from,
				length: a.length,
				type: getExtendedCommentType(payload.commentType, source),
				id: payload.id, // don't need to remap IDs since only saving on unmount
				text: payload.text,
			};
		});

	return comments;
}

export default function CommentedEssay({
	commentedEssay,
	isScoreReport = false,
	showAnnotations = true,
}: Props) {
	const role = useAppSelector((state) => state.user.role);
	const essayRef = useRef<RichTextWrapper>(null);
	const sourcesRef = useRef<RichTextWrapper>(null);

	const [callSyncDraftComments] = useSyncDraftCommentsMutation();

	useEffect(() => {
		// save teacher changes to annotations when CommentedEssay is unmounted
		const currentEssayRef = essayRef.current;
		const currentSourcesRef = sourcesRef.current;

		return () => {
			if (!isScoreReport || role != UserRole.Proctor) return;
			if (!currentEssayRef || !currentSourcesRef) return;

			const essayComments = getRteComments(currentEssayRef, "essay");
			const sourcesComments = getRteComments(currentSourcesRef, "sources");
			const payload: SyncDraftCommentRequest = {
				draftId: commentedEssay.id,
				comments: [...essayComments, ...sourcesComments],
			};

			callSyncDraftComments(payload)
				.unwrap()
				.catch((reason) => {
					console.log(`callSyncDraftComments failed: ${JSON.stringify(reason)}`);
				});
		};
	}, [essayRef, sourcesRef]);

	if (!commentedEssay) return null;

	const readonlyToolbarOptions: ToolbarOptions = {
		position: "top",
		textEditing: false,
		highlights: showAnnotations ? "view" : false,
		highlightsShown: showAnnotations,
		comments: showAnnotations
			? role === UserRole.Proctor && isScoreReport
				? "add"
				: "view"
			: false,
		commentsShown: showAnnotations,
	};

	const essayProps: RteProps = {
		readonly: true,
		disablePasteDrop: true,
		toolbarOptions: readonlyToolbarOptions,
	};

	const essayContent: RteContent = {
		format: commentedEssay.responseFormat,
		content: commentedEssay.responseContent,
		markup: commentedEssay.responseMarkup,
	};

	setRteProps(
		essayProps,
		essayContent,
		JSON.parse(commentedEssay.responseHighlights),
		JSON.parse(commentedEssay.responseComments)
	);

	const sourcesProps: RteProps = {
		readonly: true,
		disablePasteDrop: true,
		toolbarOptions: readonlyToolbarOptions,
	};

	const sourcesContent: RteContent = {
		format: commentedEssay.sourcesFormat,
		content: commentedEssay.sourcesContent,
		markup: commentedEssay.sourcesMarkup,
	};

	setRteProps(
		sourcesProps,
		sourcesContent,
		JSON.parse(commentedEssay.sourcesHighlights),
		JSON.parse(commentedEssay.sourcesComments)
	);

	return (
		<div className="commentedEssay basis-1/2 min-w-[350px]">
			<h3 className={commentedEssay.hasTitle ? "mt-1" : "text-gray-400 italic mt-1"}>
				{htmlDecode(commentedEssay.title)}
			</h3>
			<div className="essayEditor">
				<RichTextWrapper {...essayProps} contentId={commentedEssay.id} ref={essayRef} />
			</div>
			<h4 className="mt-4">Cited Sources</h4>
			<div className="sourcesEditor">
				<RichTextWrapper {...sourcesProps} contentId={commentedEssay.id} ref={sourcesRef} />
			</div>
		</div>
	);
}
